import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { firstValueFrom } from 'rxjs'
import type { Table } from 'apache-arrow'
import { fetchCube } from '../../../../Repository/Visualization/Cubes.js'
import { BlastSource } from '../../../../Model/Project/BlastSource.js'
import { filterCubeByPathAndGrade } from '../../../Component/ConfigurableVisualization/Sinks/Selector/CubeFiltering.js'
import { computeStackedBarRows } from '../../../Component/ConfigurableVisualization/Sinks/Selector/TaxaStackedBarCore.js'
import { collapseStackedSeries } from '../../../Component/ConfigurableVisualization/Sinks/Selector/CollapseSeries.js'
import { StackedBarChartNext } from '../../../Component/Charts/StackedBarChartNext.js'
import { PieChartNext } from '../../../Component/Charts/PieChartNext.js'
import { TreemapChartNext } from '../../../Component/Charts/TreemapChartNext.js'
import { IdentityKey } from '../../../Component/Charts/IdentityKey.js'
import { flatStackSeries } from '../../../Component/Charts/StackSeries.js'
import {
  CHART_SURFACE,
  DEFAULT_IDENTITY_STYLE,
  IDENTITY_COLORS,
  identityFor,
  identityKeySvg,
  type Identity,
  type IdentityStyle,
  MARKS,
  setIdentityStyle
} from '../../../Component/Charts/Options/visualIdentity.js'
import { simulate, type WorstPair, worstPair } from './colorDistance.js'

type Rows = Array<Record<string, number | string>>

const Svg = ({ markup }: { markup: string }) => <span className="inline-flex" dangerouslySetInnerHTML={{ __html: markup }} />

function PairSwatch({ pair, label }: { pair: WorstPair; label: string }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-20 shrink-0 opacity-70">{label}</span>
      <div className="flex">
        <div title={pair.a} className="w-10 h-7 rounded-l" style={{ backgroundColor: pair.a }} />
        <div title={pair.b} className="w-10 h-7 rounded-r" style={{ backgroundColor: pair.b }} />
      </div>
      {pair.vision !== 'normal' && (
        <>
          <span className="opacity-60">seen {pair.vision}:</span>
          <div className="flex">
            <div className="w-10 h-7 rounded-l" style={{ backgroundColor: simulate(pair.a, pair.vision) }} />
            <div className="w-10 h-7 rounded-r" style={{ backgroundColor: simulate(pair.b, pair.vision) }} />
          </div>
        </>
      )}
      <span className="font-mono">
        ΔE {pair.distance.toFixed(1)} · {pair.a} / {pair.b}
      </span>
    </div>
  )
}

function Palette() {
  const pairs = useMemo(() => ({ normal: worstPair(IDENTITY_COLORS, 'normal'), cb: worstPair(IDENTITY_COLORS, 'colorblind') }), [])
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1 p-2 rounded w-fit" style={{ backgroundColor: CHART_SURFACE }}>
        {IDENTITY_COLORS.map((c) => (
          <div key={c} title={c} className="w-7 h-7 rounded" style={{ backgroundColor: c }} />
        ))}
      </div>
      <div className="flex flex-col gap-1 p-2 rounded w-fit" style={{ backgroundColor: CHART_SURFACE }}>
        <PairSwatch pair={pairs.normal} label="worst pair" />
        <PairSwatch pair={pairs.cb} label="colour-blind" />
      </div>
    </div>
  )
}

function Vocabulary() {
  return (
    <div className="overflow-x-auto">
      <table className="text-xs">
        <thead>
          <tr>
            <th />
            {MARKS.map((m) => (
              <th key={m} className="text-left px-2">
                {m}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {IDENTITY_COLORS.map((color) => (
            <tr key={color}>
              <td className="font-mono pr-2">{color}</td>
              {MARKS.map((mark) => {
                const identity: Identity = { name: '', color, mark, reserved: false }
                return (
                  <td key={mark} className="p-1">
                    <div className="flex items-center gap-2 p-2 rounded" style={{ backgroundColor: CHART_SURFACE }}>
                      <Svg markup={identityKeySvg(identity, { size: 40, lineLength: 0 })} />
                      <div className="flex flex-col gap-1">
                        {[1.5, 2.5, 4].map((w) => (
                          <Svg key={w} markup={identityKeySvg(identity, { size: 0, lineLength: 70, lineWidth: w })} />
                        ))}
                      </div>
                    </div>
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function sampleBuckets(table: Table) {
  const col = table.getChild('sampleId')
  const ids = new Set<string>()
  for (let i = 0; i < table.numRows; i++) {
    const v = col?.get(i) as string | null
    if (v != null) ids.add(v)
  }
  return [...ids].sort().map((id, i) => ({ key: id, label: `S${i + 1}`, samples: [{ id }] }))
}

function ClashReport({ keys, totals, grand }: { keys: string[]; totals: Map<string, number>; grand: number }) {
  const visible = keys.map(identityFor).filter((id) => !id.reserved && (totals.get(id.name) ?? 0) >= 0.01 * grand)
  let sameColor = 0
  let sameIdentity = 0
  for (let i = 0; i < visible.length; i++) {
    for (let j = i + 1; j < visible.length; j++) {
      if (visible[i]!.color === visible[j]!.color) {
        sameColor++
        if (visible[i]!.mark === visible[j]!.mark) sameIdentity++
      }
    }
  }
  return (
    <p className="text-xs text-gray-700">
      {keys.length} series drawn, {visible.length} with ≥ 1% of reads. Among those: <b>{sameColor}</b> pairs share a colour,{' '}
      <b>{sameIdentity}</b> share colour <i>and</i> pattern.
    </p>
  )
}

function RealData() {
  const [searchParams, setSearchParams] = useSearchParams()
  const set = (key: string, value: string) => setSearchParams((sp) => ({ ...Object.fromEntries(sp), [key]: value }), { replace: true })
  const scopeId = searchParams.get('scope') ?? '638e1e55-06a4-4fa9-b13d-db22c652b176'
  const source = (searchParams.get('source') as BlastSource | null) ?? BlastSource.NCBI
  const pathParam = searchParams.get('path') ?? ''
  const path = useMemo(() => (pathParam ? pathParam.split('>') : []), [pathParam])
  const [cap, setCap] = useState(20)
  const [table, setTable] = useState<Table | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setTable(null)
    setError(null)
    firstValueFrom(fetchCube(scopeId, source))
      .then((r) => (r.kind === 'fresh' ? setTable(r.table) : setError('cube unchanged without a cached copy')))
      .catch((e: unknown) => setError(String(e)))
  }, [scopeId, source])

  const lab = useMemo(() => {
    if (!table) return null
    const filtered = filterCubeByPathAndGrade({
      table,
      selectedBuckets: sampleBuckets(table) as never,
      selectedPath: path,
      gradeThreshold: 0,
      countMode: 'READS'
    })
    const pct = computeStackedBarRows({ filteredBuckets: filtered, level: path.length, isPercent: true })
    const abs = computeStackedBarRows({ filteredBuckets: filtered, level: path.length, isPercent: false })
    const collapsed = collapseStackedSeries(pct.data as Rows, pct.stackKeys, { mode: 'topN', topN: cap, minShare: 0 })
    const totals = new Map<string, number>()
    for (const row of abs.data as Rows) {
      for (const k of abs.stackKeys) totals.set(k, (totals.get(k) ?? 0) + (Number(row[k]) || 0))
    }
    const grand = [...totals.values()].reduce((a, b) => a + b, 0)
    const slices = collapsed.stackKeys.map((name) => ({ name, value: totals.get(name) ?? 0 })).filter((s) => s.value > 0)
    return { rows: collapsed.data, keys: collapsed.stackKeys, totals, grand, slices, samples: pct.data.length }
  }, [table, path, cap])

  const byTotal = (lab?.keys ?? []).filter((k) => k !== 'Other').sort((a, b) => (lab!.totals.get(b) ?? 0) - (lab!.totals.get(a) ?? 0))

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <input
          className="input input-bordered input-xs w-80 font-mono"
          value={scopeId}
          onChange={(e) => set('scope', e.target.value)}
          aria-label="Order or project scope id"
        />
        <select className="select select-bordered select-xs" value={source} onChange={(e) => set('source', e.target.value)}>
          {Object.values(BlastSource).map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
        <label>
          series cap{' '}
          <select className="select select-bordered select-xs" value={cap} onChange={(e) => setCap(Number(e.target.value))}>
            {[12, 20, 40].map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </label>
        <button className="btn btn-xs" disabled={path.length === 0} onClick={() => set('path', path.slice(0, -1).join('>'))}>
          Back
        </button>
        <span className="font-mono">{path.join(' → ') || 'Root'}</span>
        {lab && (
          <select
            className="select select-bordered select-xs"
            value=""
            onChange={(e) => e.target.value && set('path', [...path, e.target.value].join('>'))}
          >
            <option value="">drill into…</option>
            {byTotal.map((k) => (
              <option key={k}>{k}</option>
            ))}
          </select>
        )}
        {lab && <span className="opacity-60">{lab.samples} samples</span>}
      </div>
      {error && <div className="text-error text-xs">{error}</div>}
      {!lab && !error && <div className="text-xs opacity-70">Loading cube…</div>}
      {lab && (
        <>
          <ClashReport keys={lab.keys} totals={lab.totals} grand={lab.grand} />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            {(['bar', 'line'] as const).map((variant) => (
              <div key={variant} className={variant === 'bar' ? 'h-[28rem] xl:col-span-2' : 'h-96 xl:col-span-2'}>
                <StackedBarChartNext
                  variant={variant}
                  dataKey="xAxisvalue"
                  data={lab.rows}
                  stackSeries={flatStackSeries(lab.keys)}
                  ylabel="% of reads"
                  xlabel="Sample"
                  isPercent={variant === 'bar'}
                  height="100%"
                />
              </div>
            ))}
            <PieChartNext data={lab.slices} height={360} />
            <TreemapChartNext data={lab.slices} height={360} />
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs p-2 rounded" style={{ backgroundColor: CHART_SURFACE }}>
            {[...byTotal, ...(lab.keys.includes('Other') ? ['Other'] : [])].map((k) => (
              <span key={k} className="flex items-center gap-1">
                <IdentityKey name={k} />
                {k}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

const STYLE_OPTIONS = {
  tone: ['contrast', 'tonal', 'black'],
  density: ['dense', 'sparse'],
  minShare: ['0', '0.03', '0.06'],
  solids: ['uniform', 'heavy']
} as const

function useLabStyle(): { style: IdentityStyle; key: string; set: (k: string, v: string) => void } {
  const [searchParams, setSearchParams] = useSearchParams()
  const pick = <T extends string>(k: keyof typeof STYLE_OPTIONS, fallback: T) =>
    ((STYLE_OPTIONS[k] as readonly string[]).includes(searchParams.get(k) ?? '') ? searchParams.get(k) : fallback) as T
  const style: IdentityStyle = {
    tone: pick('tone', DEFAULT_IDENTITY_STYLE.tone),
    density: pick('density', DEFAULT_IDENTITY_STYLE.density),
    minShare: Number(pick('minShare', String(DEFAULT_IDENTITY_STYLE.minShare))),
    solids: pick('solids', DEFAULT_IDENTITY_STYLE.solids)
  }
  const key = JSON.stringify(style)
  // Set synchronously so this render's charts already draw with it; the lab owns the override.
  useMemo(() => setIdentityStyle(style), [key]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => setIdentityStyle(), [])
  const set = (k: string, v: string) => setSearchParams((sp) => ({ ...Object.fromEntries(sp), [k]: v }), { replace: true })
  return { style, key, set }
}

/** `/colorcode/lab` — the chart identity vocabulary, and the real chart components on a real cube. */
export const IdentityLabPage = () => {
  const { style, key, set } = useLabStyle()
  return (
    <div className="max-w-7xl mx-auto p-4 space-y-8">
      <header className="space-y-2">
        <h1 className="text-3xl font-bold">Identity lab</h1>
        <p className="text-sm text-gray-600">
          Every taxon or trait wears one of {IDENTITY_COLORS.length} colours × {MARKS.length} patterns ={' '}
          {IDENTITY_COLORS.length * MARKS.length} identities, derived from its name alone. The charts below are the production chart
          components.
        </p>
        <div className="flex flex-wrap gap-4 text-sm">
          {(Object.keys(STYLE_OPTIONS) as Array<keyof typeof STYLE_OPTIONS>).map((k) => (
            <label key={k}>
              {k}{' '}
              <select className="select select-bordered select-sm" value={String(style[k])} onChange={(e) => set(k, e.target.value)}>
                {STYLE_OPTIONS[k].map((v) => (
                  <option key={v}>{v}</option>
                ))}
              </select>
            </label>
          ))}
        </div>
      </header>

      <section className="space-y-2">
        <h2 className="text-xl font-semibold">Palette</h2>
        <Palette />
      </section>

      <section className="space-y-2" key={`data-${key}`}>
        <h2 className="text-xl font-semibold">Real data</h2>
        <RealData />
      </section>

      <section className="space-y-2" key={`vocab-${key}`}>
        <h2 className="text-xl font-semibold">Vocabulary at true size</h2>
        <p className="text-xs text-gray-600">Per cell: a 40px fill, then lines at 1.5 / 2.5 / 4px.</p>
        <Vocabulary />
      </section>
    </div>
  )
}

export default IdentityLabPage
